import React, {useEffect, useState} from 'react';
import {Alert, Badge, Button, Card, Form, Modal, Spinner, Table} from 'react-bootstrap';
import axios from './axiosConfig';
import './APIKeyPage.css';

interface APIKeyItem {
    id: number;
    name: string;
    key_prefix: string;
    masked_key: string;
    scopes: string[];
    status: string;
    expires_at?: string;
    last_used_at?: string;
    revoked_at?: string;
    created_by_user_id: number;
    created_at: string;
    updated_at: string;
}

interface CreateAPIKeyResponse {
    id: number;
    name: string;
    api_key: string;
    key_prefix: string;
    masked_key: string;
    scopes: string[];
    status: string;
    expires_at?: string;
    message: string;
}

const APIKeyPage: React.FC = () => {
    const [apiKeys, setApiKeys] = useState<APIKeyItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [creating, setCreating] = useState<boolean>(false);
    const [revokingId, setRevokingId] = useState<number | null>(null);

    const [error, setError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');

    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [keyName, setKeyName] = useState<string>('');
    const [expiresAt, setExpiresAt] = useState<string>('');

    const [showCreatedKeyModal, setShowCreatedKeyModal] = useState<boolean>(false);
    const [createdKey, setCreatedKey] = useState<CreateAPIKeyResponse | null>(null);
    const [copySuccess, setCopySuccess] = useState<string>('');

    useEffect(() => {
        fetchAPIKeys();
    }, []);

    const fetchAPIKeys = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await axios.get<APIKeyItem[]>('/api/api-keys');
            setApiKeys(response.data || []);
        } catch (err: any) {
            console.error('Failed to fetch API keys:', err);
            setError(err.response?.data?.error || 'Failed to fetch API keys.');
        } finally {
            setLoading(false);
        }
    };

    const resetCreateForm = () => {
        setKeyName('');
        setExpiresAt('');
        setError('');
        setSuccessMessage('');
    };

    const openCreateModal = () => {
        resetCreateForm();
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        if (creating) {
            return;
        }

        setShowCreateModal(false);
        resetCreateForm();
    };

    const buildExpiresAtPayload = (): string | undefined => {
        if (!expiresAt) {
            return undefined;
        }

        const date = new Date(expiresAt);
        if (Number.isNaN(date.getTime())) {
            return undefined;
        }

        return date.toISOString();
    };

    const handleCreateAPIKey = async (e: React.FormEvent) => {
        e.preventDefault();

        const trimmedName = keyName.trim();
        if (!trimmedName) {
            setError('API key name is required.');
            return;
        }

        setCreating(true);
        setError('');
        setSuccessMessage('');

        try {
            const payload: {
                name: string;
                expires_at?: string;
            } = {
                name: trimmedName,
            };

            const expiresAtPayload = buildExpiresAtPayload();
            if (expiresAtPayload) {
                payload.expires_at = expiresAtPayload;
            }

            const response = await axios.post<CreateAPIKeyResponse>('/api/api-keys', payload);

            setCreatedKey(response.data);
            setShowCreateModal(false);
            setShowCreatedKeyModal(true);
            setCopySuccess('');
            resetCreateForm();

            await fetchAPIKeys();
        } catch (err: any) {
            console.error('Failed to create API key:', err);
            setError(err.response?.data?.error || 'Failed to create API key.');
        } finally {
            setCreating(false);
        }
    };

    const handleRevokeAPIKey = async (apiKey: APIKeyItem) => {
        const confirmed = window.confirm(
            `Are you sure you want to revoke "${apiKey.name}"? This action cannot be undone.`
        );

        if (!confirmed) {
            return;
        }

        setRevokingId(apiKey.id);
        setError('');
        setSuccessMessage('');

        try {
            await axios.patch(`/api/api-keys/${apiKey.id}/revoke`);
            setSuccessMessage('API key revoked successfully.');
            await fetchAPIKeys();
        } catch (err: any) {
            console.error('Failed to revoke API key:', err);
            setError(err.response?.data?.error || 'Failed to revoke API key.');
        } finally {
            setRevokingId(null);
        }
    };

    const copyTextToClipboard = async (text: string) => {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return;
        }

        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (!successful) {
            throw new Error('Copy command failed');
        }
    };

    const handleCopyKey = async () => {
        if (!createdKey?.api_key) {
            return;
        }

        try {
            await copyTextToClipboard(createdKey.api_key);
            setCopySuccess('Copied to clipboard.');
        } catch (err) {
            console.error('Failed to copy API key:', err);
            setCopySuccess('Copy failed. Please copy the key manually.');
        }
    };

    const closeCreatedKeyModal = () => {
        setShowCreatedKeyModal(false);
        setCreatedKey(null);
        setCopySuccess('');
    };

    const formatDateTime = (value?: string): string => {
        if (!value) {
            return '-';
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleString();
    };

    const getStatusBadge = (status: string) => {
        const normalizedStatus = status.toLowerCase();

        if (normalizedStatus === 'active') {
            return <Badge bg="success">Active</Badge>;
        }

        if (normalizedStatus === 'revoked') {
            return <Badge bg="secondary">Revoked</Badge>;
        }

        if (normalizedStatus === 'expired') {
            return <Badge bg="warning" text="dark">Expired</Badge>;
        }

        return <Badge bg="light" text="dark">{status}</Badge>;
    };

    return (
        <div className="api-key-page">
            <div className="api-key-header">
                <div>
                    <h2>API Keys</h2>
                    <p className="api-key-subtitle">
                        Create and manage API keys for external integrations.
                    </p>
                </div>

                <Button variant="primary" onClick={openCreateModal}>
                    Create API Key
                </Button>
            </div>

            {error && !showCreateModal && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
                    {successMessage}
                </Alert>
            )}

            <Card className="api-key-card">
                <Card.Body>
                    {loading ? (
                        <div className="api-key-loading">
                            <Spinner animation="border" size="sm"/>
                            <span>Loading API keys...</span>
                        </div>
                    ) : (
                        <div className="api-key-table-wrapper">
                            <Table responsive hover className="api-key-table">
                                <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Key</th>
                                    <th>Status</th>
                                    <th>Scopes</th>
                                    <th>Expires At</th>
                                    <th>Last Used At</th>
                                    <th>Created At</th>
                                    <th>Action</th>
                                </tr>
                                </thead>

                                <tbody>
                                {apiKeys.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="api-key-empty">
                                            No API keys found.
                                        </td>
                                    </tr>
                                ) : (
                                    apiKeys.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.name}</td>
                                            <td>
                                                <code>{item.masked_key || item.key_prefix}</code>
                                            </td>
                                            <td>{getStatusBadge(item.status)}</td>
                                            <td>
                                                {item.scopes && item.scopes.length > 0 ? (
                                                    item.scopes.map((scope) => (
                                                        <Badge key={scope} bg="info" className="api-key-scope">
                                                            {scope}
                                                        </Badge>
                                                    ))
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                            <td>{formatDateTime(item.expires_at)}</td>
                                            <td>{formatDateTime(item.last_used_at)}</td>
                                            <td>{formatDateTime(item.created_at)}</td>
                                            <td>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    disabled={item.status === 'revoked' || revokingId === item.id}
                                                    onClick={() => handleRevokeAPIKey(item)}
                                                >
                                                    {revokingId === item.id ? 'Revoking...' : 'Revoke'}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Modal
                show={showCreateModal}
                onHide={closeCreateModal}
                centered
                className="api-key-modal"
            >
                <Form onSubmit={handleCreateAPIKey}>
                    <Modal.Header closeButton={!creating}>
                        <Modal.Title>Create API Key</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {error && (
                            <Alert variant="danger" onClose={() => setError('')} dismissible>
                                {error}
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Key Name</Form.Label>
                            <Form.Control
                                type="text"
                                value={keyName}
                                onChange={(e) => setKeyName(e.target.value)}
                                placeholder="Customer Integration Key"
                                disabled={creating}
                                required
                            />
                            <Form.Text className="text-muted">
                                Use a clear name to identify where this key is used.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Expires At</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                value={expiresAt}
                                onChange={(e) => setExpiresAt(e.target.value)}
                                disabled={creating}
                            />
                            <Form.Text className="text-muted">
                                Optional. Leave empty if you do not want to set an expiration time.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeCreateModal} disabled={creating}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={creating}>
                            {creating ? 'Creating...' : 'Create'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal
                show={showCreatedKeyModal}
                onHide={closeCreatedKeyModal}
                centered
                backdrop="static"
                className="api-key-modal"
            >
                <Modal.Header>
                    <Modal.Title>API Key Created</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Alert variant="warning">
                        Please copy this API key now. It will not be shown again.
                    </Alert>

                    <div className="created-key-box">
                        <code>{createdKey?.api_key}</code>
                    </div>

                    {copySuccess && (
                        <div className="copy-message">
                            {copySuccess}
                        </div>
                    )}

                    <div className="created-key-info">
                        <div>
                            <strong>Name:</strong> {createdKey?.name}
                        </div>
                        <div>
                            <strong>Status:</strong> {createdKey?.status}
                        </div>
                        <div>
                            <strong>Expires At:</strong> {formatDateTime(createdKey?.expires_at)}
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="outline-primary" onClick={handleCopyKey}>
                        Copy API Key
                    </Button>
                    <Button variant="primary" onClick={closeCreatedKeyModal}>
                        I have copied the key
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default APIKeyPage;